import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, X } from 'lucide-react';
import { AiChatWindow } from './ai-chat-window';
import { useSidebarStore } from '@/stores/sidebar-store';
import { cn } from '@/lib/utils';

/** Launcher offsets keeping the bubble clear of the map's corner controls
 * (compass + fullscreen) and the bottom-right HUD dock zone. */
const BUBBLE_RIGHT = 16;
const BUBBLE_BOTTOM = 108;
const WINDOW_WIDTH = 380;

/**
 * Floating VTAK AI launcher pinned to the bottom-right of the map. Clicking
 * the bubble opens the chat window anchored above it; clicking again — or the
 * window's close button — dismisses it. Opening the bubble closes the sidebar
 * AI panel (and vice versa) so only one chat surface is mounted at a time.
 */
export function AiChatBubble(): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const activePanel = useSidebarStore((s) => s.activePanel);
  const setActivePanel = useSidebarStore((s) => s.setActivePanel);

  // If the user opens the sidebar AI panel, yield to it.
  useEffect(() => {
    if (activePanel === 'ai') setOpen(false);
  }, [activePanel]);

  function toggle(): void {
    setOpen((wasOpen) => {
      if (!wasOpen && useSidebarStore.getState().activePanel === 'ai') {
        setActivePanel(null);
      }
      return !wasOpen;
    });
  }

  return (
    <>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 8 }}
            transition={{ duration: 0.18, ease: 'easeOut' }}
            style={{
              position: 'fixed',
              right: BUBBLE_RIGHT,
              bottom: BUBBLE_BOTTOM + 60,
              width: WINDOW_WIDTH,
              height: 'min(600px, calc(100vh - 240px))',
              transformOrigin: 'bottom right',
            }}
            className="z-[1005] overflow-hidden rounded-xl border border-echo-border bg-echo-grey/95 shadow-2xl ring-1 ring-black/20 backdrop-blur"
          >
            <AiChatWindow onClose={() => setOpen(false)} />
          </motion.div>
        )}
      </AnimatePresence>

      <motion.button
        type="button"
        onClick={toggle}
        aria-label={open ? 'Close VTAK AI chat' : 'Open VTAK AI chat'}
        title="VTAK AI"
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        style={{ position: 'fixed', right: BUBBLE_RIGHT, bottom: BUBBLE_BOTTOM }}
        className={cn(
          'z-[1005] flex h-12 w-12 items-center justify-center rounded-full border bg-echo-grey/92 shadow-2xl ring-1 ring-black/30 backdrop-blur transition-[border-color,box-shadow]',
          open
            ? 'border-ai/70 shadow-[0_0_18px_rgba(76,175,80,0.25)]'
            : 'border-echo-border hover:border-ai/60 hover:shadow-[0_0_18px_rgba(76,175,80,0.2)]',
        )}
      >
        <AnimatePresence mode="wait" initial={false}>
          <motion.span
            key={open ? 'close' : 'open'}
            initial={{ opacity: 0, rotate: -45 }}
            animate={{ opacity: 1, rotate: 0 }}
            exit={{ opacity: 0, rotate: 45 }}
            transition={{ duration: 0.12 }}
            className="flex"
          >
            {open ? (
              <X className="h-5 w-5 text-muted-foreground" />
            ) : (
              <Sparkles className="h-5 w-5 text-ai" />
            )}
          </motion.span>
        </AnimatePresence>
        {!open && (
          <span className="absolute right-0.5 top-0.5 h-2 w-2 animate-pulse rounded-full bg-ai ring-2 ring-echo-grey" />
        )}
      </motion.button>
    </>
  );
}
